import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useLocation } from "wouter";
import {
  getStatusColorHex,
  getStatusBadgeClass,
  getStatusText,
  getStatusPriority,
} from "@/lib/status-utils.js";
import { getStatusDisplayText } from "@/lib/formatting-utils.js";
import { useAssets, useFilteredAssets } from "@/contexts/AssetContext.jsx";
import { Button } from "@/components/ui/button.jsx";
import { RotateCcw } from "lucide-react";
import { arrayUtils, mathUtils } from "@/lib/utils.js";
import { formatTimestamp, formatFaultTime } from "@/lib/datetime-utils.js";
import {
  analyzeDataDistribution,
  getClusteringThreshold,
  getDataDependentThreshold,
  shouldDisableClustering,
  calculateOptimalBounds,
  getFitBoundsOptions,
  createStatusIcon,
  createClusterIcon,
  calculateDistance,
  clusterCompressors,
} from "@/lib/map-utils.js";
import { copyCoordinatesToClipboard } from "@/components/copy-coordinates-button.jsx";
import {
  mapPopupActionButton,
  outlineChromeSurface,
  steelChromeButton,
} from "@/lib/status-styles.js";

import { useLeafletReady } from "@/hooks/useLeafletReady.js";

/**
 * @param {import("leaflet").Map|null|undefined} map
 * @param {import("react").RefObject<import("leaflet").Map|null>} mapRef
 * @returns {boolean}
 */
function isLeafletMapUsable(map, mapRef) {
  if (!map || mapRef.current !== map) {
    return false;
  }
  const container = map.getContainer?.();
  return Boolean(container?.isConnected);
}

/**
 * Fleet Map Component
 * Displays an interactive map with compressor markers and clustering
 */
function FleetMap({
  filters = {
    vendor: [],
    location: [],
    telemetrySource: [],
    optimizationEnabled: null,
    status: [],
  },
}) {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const isMountedRef = useRef(true);
  const isRenderingRef = useRef(false);
  const interactionTimeoutRef = useRef(null);

  const clearInteractionTimeout = () => {
    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
      interactionTimeoutRef.current = null;
    }
  };
  const [, setLocation] = useLocation();
  // Use a stable callback that always has access to the latest setLocation
  const navigateToAsset = useCallback(
    (assetId) => {
      if (typeof setLocation === "function") {
        setLocation(`/asset/${encodeURIComponent(assetId)}/overview?from=map`);
      } else {
        window.location.href = `/asset/${encodeURIComponent(assetId)}/overview?from=map`;
      }
    },
    [setLocation]
  );

  const urlParams = new URLSearchParams(window.location.search);
  const fromMap = urlParams.get("from") === "map";

  const leafletReady = useLeafletReady();

  /** @type {[number, Function]} */
  const [currentZoom, setCurrentZoom] = useState(14);

  /** @type {[boolean, Function]} */
  const [hasInitialFit, setHasInitialFit] = useState(false);

  /** @type {[boolean, Function]} */
  const [isReturningFromPage, setIsReturningFromPage] = useState(false);

  /** @type {[Object, Function]} */
  const [mapState, setMapState] = useState(null);

  /** @type {[boolean, Function]} */
  const [userInteracting, setUserInteracting] = useState(false);

  /** @type {[number, Function]} */
  const [lastFilterChange, setLastFilterChange] = useState(0);

  /** @type {[number, Function]} */
  const [initialClusterCount, setInitialClusterCount] = useState(null);

  const { assets: compressors, isLoading } = useAssets();
  const { filteredAssets: filteredCompressors } = useFilteredAssets(
    filters,
    ""
  );

  const popupMaxHeight = useMemo(() => {
    if (mapRef.current) {
      const container = mapRef.current;
      const height = container.offsetHeight;
      return Math.max(200, Math.min(400, Math.round(height * 0.6)));
    }
    return 300;
  }, []);

  const createPopupContent = useCallback(
    (compressor) => {
      return `
      <div class="bg-card text-card-foreground" style="min-width: 280px; max-height: ${popupMaxHeight}px; display: flex; flex-direction: column;">
                  <!-- Header matching table header styling -->
                  <div class="bg-card px-4 py-3 border-b border-border">
                    <h3 class="text-lg font-light text-foreground">Asset Details</h3>
                  </div>
                  
                  <!-- Scrollable content area -->
                  <div style="flex: 1; overflow-y: auto;">
                    <!-- Asset Number Row -->
                    <div class="border-b border-border px-4 py-3 hover:bg-muted">
                      <div class="space-y-1">
                        <span class="text-muted-foreground font-light text-sm">Asset Number</span>
                        <div class="text-sm font-medium text-foreground">
                          ${compressor.assetNumber}
                        </div>
                      </div>
                    </div>
                    
                    <!-- Status Row -->
                    <div class="border-b border-border px-4 py-3 hover:bg-muted">
                      <div class="space-y-1">
                        <span class="text-muted-foreground font-light text-sm">Status</span>
                        <div class="flex items-center rounded-full px-3 py-1 text-sm font-semibold w-fit ${getStatusBadgeClass(compressor.status)}">
                          <div class="w-2 h-2 rounded-full mr-2" style="background-color: ${getStatusColorHex(compressor.status)};"></div>
                          ${getStatusDisplayText(compressor.status)}
                        </div>
                      </div>
                    </div>
                    
                    <!-- Vendor Row -->
                    <div class="border-b border-border px-4 py-3 hover:bg-muted">
                      <div class="space-y-1">
                        <span class="text-muted-foreground font-light text-sm">Vendor</span>
                        <div class="text-sm font-medium text-foreground">
                          ${compressor.vendor}
                        </div>
                      </div>
                    </div>
                    
                    <!-- Location Row -->
                    <div class="border-b border-border px-4 py-3 hover:bg-muted">
                      <div class="space-y-1">
                        <span class="text-muted-foreground font-light text-sm">Location</span>
                        <div class="text-sm font-medium text-foreground">
                          ${compressor.location}
                        </div>
                      </div>
                    </div>
                    
                    <!-- Last Fault Row -->
                    <div class="border-b border-border px-4 py-3 hover:bg-muted">
                      <div class="space-y-1">
                        <span class="text-muted-foreground font-light text-sm">Last Fault</span>
                        <div class="text-sm font-medium text-foreground">
                          ${compressor.lastFault || "No faults"}
                        </div>
                        ${compressor.lastFaultTime ? `<div class="text-xs text-muted-foreground">${formatFaultTime(compressor.lastFaultTime)}</div>` : ""}
                      </div>
                    </div>
                    
                    <!-- Last Updated Row -->
                    <div class="px-4 py-3 hover:bg-muted">
                      <div class="space-y-1">
                        <span class="text-muted-foreground font-light text-sm">Last Updated</span>
                        <div class="text-sm font-medium text-foreground">${formatTimestamp(compressor.lastUpdated)}</div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Footer with action buttons -->
                  <div class="bg-muted px-4 py-3 border-t border-border">
                    <div class="flex gap-2">
                      <button 
                        class="${mapPopupActionButton} ${outlineChromeSurface}"
                        data-testid="button-view-details-${compressor.id}"
                      >
                        <span class="inline-flex items-center justify-center w-full">
                          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                          View
                        </span>
                      </button>
                      ${
                        compressor.latitude != null
                        && compressor.longitude != null
                          ? `
                      <button 
                        class="${mapPopupActionButton} ${steelChromeButton}"
                        data-testid="button-copy-coords-${compressor.id}"
                        data-lat="${compressor.latitude}"
                        data-lng="${compressor.longitude}"
                      >
                        <span class="inline-flex items-center justify-center w-full copy-coords-content">
                          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                          </svg>
                          Copy Coords
                        </span>
                      </button>
                      `
                          : ""
                      }
                    </div>
                  </div>
                </div>
              `;
    },
    [popupMaxHeight]
  );

  const createClusterPopupContent = useCallback((cluster) => {
    const statusCounts = cluster.compressors.reduce((counts, comp) => {
      const statusLabel = getStatusText(comp.status);
      counts[statusLabel] = (counts[statusLabel] || 0) + 1;
      return counts;
    }, {});

    const statusBreakdown = Object.entries(statusCounts)
      .map(
        ([status, count]) =>
          `<div class="flex justify-between"><span>${status}:</span><span>${count}</span></div>`
      )
      .join("");

    return `
      <div class="p-3 min-w-48 text-foreground">
        <div class="font-semibold text-base mb-2 text-foreground">Asset Cluster</div>
        <div class="mb-2 text-sm">
          <span class="font-medium">Total Assets:</span> ${cluster.compressors.length}
        </div>
        <div class="mb-2 text-sm">
          <span class="font-medium">Status Breakdown:</span>
        </div>
        <div class="pl-2 space-y-1 text-sm text-muted-foreground">
            ${statusBreakdown}
        </div>
      </div>
    `;
  }, []);

  const saveMapState = (zoom, center, filters) => {
    if (
      !center
      || typeof center.lat !== "number"
      || typeof center.lng !== "number"
    ) {
      return;
    }

    const state = {
      zoom: Math.max(0, Math.min(18, zoom)), // Clamp zoom to valid range
      center: [center.lat, center.lng],
      filters,
      timestamp: Date.now(),
    };
    localStorage.setItem("map-clustering-state", JSON.stringify(state));
  };

  const loadMapState = () => {
    const saved = localStorage.getItem("map-clustering-state");
    if (!saved) {
      return null;
    }

    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  };

  const clearMapState = () => {
    localStorage.removeItem("map-clustering-state");
  };

  useEffect(() => {
    const savedState = loadMapState();
    if (savedState) {
      setMapState(savedState);
      setCurrentZoom(savedState.zoom);
    }

    if (fromMap) {
      setIsReturningFromPage(true);
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete("from");
      window.history.replaceState({}, "", newUrl);
    }

    return () => {
      isMountedRef.current = false;
      clearInteractionTimeout();
    };
  }, [fromMap]);

  useEffect(() => {
    const cleanupMap = () => {
      clearInteractionTimeout();

      if (leafletMapRef.current) {
        try {
          const map = leafletMapRef.current;

          if (map._saveTimeout) {
            clearTimeout(map._saveTimeout);
            map._saveTimeout = null;
          }

          if (map._fadeAnimated !== undefined) {
            map._fadeAnimated = false;
          }

          if (map.stop) {
            map.stop();
          }

          if (map.off) {
            map.off();
          }

          if (map.eachLayer) {
            try {
              map.eachLayer((layer) => {
                try {
                  map.removeLayer(layer);
                } catch (e) {}
              });
            } catch (e) {}
          }

          const container = map.getContainer?.();
          if (container && container.parentNode) {
            container.innerHTML = "";
          }

          map.remove();
        } catch (error) {}
        leafletMapRef.current = null;
      }
    };

    const initializeMap = () => {
      if (!mapRef.current || typeof window === "undefined" || !window.L) {
        return;
      }

      cleanupMap();

      const L = window.L;
      const container = mapRef.current;

      if (
        !container.offsetParent
        || container.offsetWidth === 0
        || container.offsetHeight === 0
      ) {
        setTimeout(initializeMap, 100);
        return;
      }

      try {
        container.innerHTML = "";

        const map = L.map(container, {
          zoomControl: true,
          attributionControl: false,
          fadeAnimation: true,
          zoomAnimation: true,
          markerZoomAnimation: true,
          zoomAnimationThreshold: 4,
        });

        const savedState = loadMapState();
        if (savedState && savedState.center && savedState.zoom) {
          map.setView(savedState.center, savedState.zoom);
        } else {
          map.setView([35.0, -97.0], 5);
        }

        const handleMoveEnd = () => {
          if (!isMountedRef.current) return;

          clearInteractionTimeout();
          interactionTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              setUserInteracting(false);
            }
          }, 150);
        };

        map.on("zoomstart", () => {
          setUserInteracting(true);
          clearInteractionTimeout();
        });

        map.on("zoomend", () => {
          if (!isMountedRef.current) return;
          const zoom = map.getZoom();
          setCurrentZoom(zoom);
          setUserInteracting(false);
        });

        map.on("movestart", () => {
          setUserInteracting(true);
        });

        map.on("moveend", () => {
          handleMoveEnd();

          if (map._saveTimeout) {
            clearTimeout(map._saveTimeout);
          }
          map._saveTimeout = setTimeout(() => {
            if (!isLeafletMapUsable(map, leafletMapRef)) {
              return;
            }
            try {
              saveMapState(map.getZoom(), map.getCenter(), filters);
            } catch (error) {}
          }, 500);
        });

        leafletMapRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution:
            '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 18,
          subdomains: "abc",
          noWrap: true,
          updateWhenZooming: false,
          updateWhenIdle: true,
          keepBuffer: 2,
          referrerPolicy: "strict-origin-when-cross-origin",
        }).addTo(map);

        L.control.scale().addTo(map);

        setTimeout(() => {
          if (
            leafletMapRef.current
            && leafletMapRef.current._container
            && leafletMapRef.current._container.offsetParent
          ) {
            leafletMapRef.current.invalidateSize({ animate: false });
          }
        }, 100);
      } catch (error) {
        setTimeout(initializeMap, 500);
      }
    };

    if (leafletReady && mapRef.current) {
      initializeMap();
    }

    return cleanupMap;
  }, [leafletReady, mapState]);

  const compressorsWithCoords = useMemo(() => {
    return filteredCompressors
      .filter(
        (compressor) =>
          compressor.latitude != null && compressor.longitude != null
      )
      .map((compressor) => ({
        ...compressor,
        lat: compressor.latitude,
        lng: compressor.longitude,
      }));
  }, [filteredCompressors]);

  const dataAnalysis = useMemo(() => {
    return analyzeDataDistribution(compressorsWithCoords);
  }, [compressorsWithCoords]);

  const clusteringConfig = useMemo(() => {
    const disableClustering = shouldDisableClustering(
      currentZoom,
      dataAnalysis
    );
    const clusteringZoom = currentZoom;

    return { disableClustering, clusteringZoom };
  }, [currentZoom, dataAnalysis, isReturningFromPage]);

  const allClusters = useMemo(() => {
    return clusterCompressors(
      compressorsWithCoords,
      clusteringConfig.clusteringZoom,
      clusteringConfig.disableClustering,
      dataAnalysis
    );
  }, [
    compressorsWithCoords,
    clusteringConfig,
    dataAnalysis,
    isReturningFromPage,
  ]);

  const visibleClusters = useMemo(() => {
    if (!leafletMapRef.current || allClusters.length < 200) {
      return allClusters;
    }

    try {
      const map = leafletMapRef.current;
      const bounds = map.getBounds();

      return allClusters.filter((cluster) => {
        const [lat, lng] = cluster.center;
        return bounds.contains([lat, lng]);
      });
    } catch {
      return allClusters;
    }
  }, [allClusters, currentZoom, userInteracting, isReturningFromPage]);

  useEffect(() => {
    setLastFilterChange(Date.now());
    setInitialClusterCount(null);

    if (isReturningFromPage) {
      setIsReturningFromPage(false);
    }
  }, [filters, isReturningFromPage]);

  useEffect(() => {
    if (isReturningFromPage && leafletMapRef.current) {
      setTimeout(() => {
        const map = leafletMapRef.current;
        if (!isLeafletMapUsable(map, leafletMapRef)) {
          return;
        }
        map.invalidateSize();
        const currentZoom = map.getZoom();
        map.setZoom(currentZoom);
      }, 100);
    }
  }, [isReturningFromPage]);

  useEffect(() => {
    /** @type {any} */ (window).handleCompressorClick =
      /** @param {string} id */ (id) => {
        setIsReturningFromPage(true);

        if (leafletMapRef.current) {
          const map = leafletMapRef.current;
          if (!isLeafletMapUsable(map, leafletMapRef)) {
            return;
          }
          const currentZoom = map.getZoom();
          const currentCenter = map.getCenter();
          saveMapState(currentZoom, currentCenter, filters);
        }

        navigateToAsset(id);
      };

    const handleBeforeUnload = () => {
      setIsReturningFromPage(true);
      if (leafletMapRef.current) {
        const map = leafletMapRef.current;
        if (isLeafletMapUsable(map, leafletMapRef)) {
          const currentZoom = map.getZoom();
          const currentCenter = map.getCenter();
          saveMapState(currentZoom, currentCenter, filters);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [filters, navigateToAsset]);

  useEffect(() => {
    const renderTimeout = setTimeout(() => {
      const renderMarkers = () => {
        if (
          !isMountedRef.current
          || !leafletMapRef.current
          || !compressors?.length
          || !window.L
        ) {
          return;
        }

        if (isRenderingRef.current) {
          return;
        }
        isRenderingRef.current = true;

        const map = leafletMapRef.current;
        const L = window.L;

        if (userInteracting) {
          isRenderingRef.current = false;
          return;
        }
        map.eachLayer((layer) => {
          if (layer instanceof L.Marker) {
            map.removeLayer(layer);
          }
        });

        if (initialClusterCount === null) {
          setInitialClusterCount(allClusters.length);
        }

        const savedState = loadMapState();
        if (
          arrayUtils.hasItems(allClusters)
          && !hasInitialFit
          && !userInteracting
          && !savedState
        ) {
          const bounds = calculateOptimalBounds(allClusters, dataAnalysis);
          const fitOptions = {
            ...getFitBoundsOptions(dataAnalysis),
            animate: true,
            duration: 1.2,
            easeLinearity: 0.1,
          };
          map.fitBounds(bounds, fitOptions);
          if (isMountedRef.current) setHasInitialFit(true);
        } else if (savedState) {
          if (isMountedRef.current) setHasInitialFit(true);
        }

        visibleClusters.forEach((cluster) => {
          const isCluster =
            arrayUtils.hasItems(cluster.compressors)
            && cluster.compressors.length > 1;

          const recalculatedStatus = cluster.compressors.reduce(
            (prev, curr) => {
              const prevPriority = getStatusPriority(prev) || 0;
              const currPriority = getStatusPriority(curr.status) || 0;
              return currPriority > prevPriority ? curr.status : prev;
            }
          );

          const markerStatus = isCluster
            ? recalculatedStatus
            : cluster.compressors[0].status;

          const marker = L.marker(cluster.center, {
            icon: isCluster
              ? createClusterIcon(cluster.compressors.length, markerStatus)
              : createStatusIcon(markerStatus),
          });

          const popupContent = isCluster
            ? createClusterPopupContent(cluster)
            : createPopupContent(cluster.compressors[0]);

          marker.bindPopup(popupContent, {
            className: "compressor-popup responsive-popup",
          });

          const compressor = cluster.compressors[0];
          const assetId = compressor.id;

          // Create click handler function
          const handleViewClick = (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (typeof window.handleCompressorClick === "function") {
              window.handleCompressorClick(assetId);
            } else {
              navigateToAsset(assetId);
            }
          };

          // Create copy coordinates handler
          const handleCopyClick = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const btn = e.currentTarget;
            const lat = parseFloat(btn.dataset.lat);
            const lng = parseFloat(btn.dataset.lng);

            const success = await copyCoordinatesToClipboard(lat, lng);

            if (success) {
              // Update button to show copied state
              const content = btn.querySelector(".copy-coords-content");
              if (content) {
                const originalHTML = content.innerHTML;
                content.innerHTML = `
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Copied!
                `;
                setTimeout(() => {
                  content.innerHTML = originalHTML;
                }, 2000);
              }
            }
          };

          marker.on("popupopen", () => {
            const popup = marker.getPopup();
            const popupElement = popup.getElement();
            if (popupElement) {
              const button = popupElement.querySelector(
                `[data-testid="button-view-details-${compressor.id}"]`
              );
              if (!button) {
                const fallbackButton = popupElement.querySelector("button");
                if (fallbackButton) {
                  fallbackButton.addEventListener("click", handleViewClick);
                }
              } else {
                button.addEventListener("click", handleViewClick);
              }

              // Add copy coordinates handler
              const copyButton = popupElement.querySelector(
                `[data-testid="button-copy-coords-${compressor.id}"]`
              );
              if (copyButton) {
                copyButton.addEventListener("click", handleCopyClick);
              }
            }
          });

          marker.on("popupclose", () => {
            const popup = marker.getPopup();
            const popupElement = popup?.getElement();
            if (popupElement) {
              const button = popupElement.querySelector(
                `[data-testid="button-view-details-${compressor.id}"]`
              );
              if (button) {
                button.removeEventListener("click", handleViewClick);
              } else {
                const fallbackButton = popupElement.querySelector("button");
                if (fallbackButton) {
                  fallbackButton.removeEventListener("click", handleViewClick);
                }
              }

              // Remove copy coordinates handler
              const copyButton = popupElement.querySelector(
                `[data-testid="button-copy-coords-${compressor.id}"]`
              );
              if (copyButton) {
                copyButton.removeEventListener("click", handleCopyClick);
              }
            }
          });

          if (isCluster) {
            let isClicking = false;

            marker.on("mouseover", (e) => {
              e.originalEvent?.stopPropagation();
              if (!isClicking) {
                marker.openPopup();
              }
            });

            marker.on("mouseout", (e) => {
              e.originalEvent?.stopPropagation();
              if (!isClicking) {
                marker.closePopup();
              }
            });

            marker.on("click", (e) => {
              if (isClicking) return;
              isClicking = true;

              e.originalEvent?.preventDefault();
              e.originalEvent?.stopPropagation();

              if (marker.isPopupOpen()) {
                marker.closePopup();
              }

              const compressorLatLngs = cluster.compressors.map((comp) => [
                comp.lat,
                comp.lng,
              ]);

              if (compressorLatLngs.length === 0) {
                return;
              }

              const bounds = L.latLngBounds(compressorLatLngs);

              if (!bounds.isValid()) {
                return;
              }

              const currentZoom = map.getZoom();
              const targetZoom = Math.max(15, currentZoom + 3);

              map.fitBounds(bounds, {
                padding: [20, 20],
                maxZoom: Math.min(targetZoom, 18),
                animate: true,
                duration: 0.8,
                easeLinearity: 0.1,
              });

              setTimeout(() => {
                isClicking = false;
              }, 1000);
            });
          }

          marker.addTo(map);
        });

        isRenderingRef.current = false;
      };

      renderMarkers();

      const retryTimeout = setTimeout(renderMarkers, 500);

      return () => clearTimeout(retryTimeout);
    }, 300);

    return () => clearTimeout(renderTimeout);
  }, [
    visibleClusters,
    allClusters,
    dataAnalysis,
    hasInitialFit,
    leafletReady,
    userInteracting,
    initialClusterCount,
    createPopupContent,
    createClusterPopupContent,
    isReturningFromPage,
    navigateToAsset,
  ]);

  return (
    <div className='relative w-full h-full z-10'>
      <style jsx='true'>{`
        .custom-marker:hover div,
        .custom-cluster-marker:hover div {
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
        }

        .custom-marker div,
        .custom-cluster-marker div {
          cursor: pointer;
        }

        .leaflet-popup-content-wrapper {
          border-radius: 8px !important;
        }

        .compressor-popup .leaflet-popup-content-wrapper {
          padding: 0 !important;
        }

        .compressor-popup .leaflet-popup-content {
          margin: 0 !important;
        }
      `}</style>
      <div
        ref={mapRef}
        className='w-full h-full transition-all duration-300 ease-in-out'
        style={{
          minHeight: "400px",
          height: "calc(100vh - 400px)",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      />

      {isLoading && (
        <div className='absolute inset-0 bg-background flex items-center justify-center z-10 transition-opacity duration-300 ease-in-out'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4'></div>
            <p className='text-muted-foreground'>Loading map...</p>
          </div>
        </div>
      )}

      {!leafletReady && !isLoading && (
        <div className='absolute inset-0 bg-background flex items-center justify-center z-10 transition-opacity duration-300 ease-in-out'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4'></div>
            <p className='text-muted-foreground'>Loading map library...</p>
          </div>
        </div>
      )}

      <div className='absolute top-4 right-4 z-map-control'>
        <Button
          variant='outline'
          size='sm'
          className='flex items-center gap-2 bg-card shadow-sm border border-border hover:bg-muted text-foreground hover:text-foreground transition-all duration-200 ease-in-out hover:shadow-md hover:scale-105'
          onClick={() => {
            if (!leafletMapRef.current || !compressorsWithCoords?.length) {
              return;
            }

            clearMapState();

            if (!arrayUtils.hasItems(compressorsWithCoords)) {
              return;
            }

            const resetZoom = Math.max(3, currentZoom - 2);
            const disableClustering = false;

            setLastFilterChange(Date.now());

            const resetClusters = clusterCompressors(
              compressorsWithCoords,
              resetZoom,
              disableClustering,
              dataAnalysis
            );

            if (arrayUtils.hasItems(resetClusters)) {
              const bounds = calculateOptimalBounds(
                resetClusters,
                dataAnalysis
              );

              if (!bounds || !bounds.isValid()) {
                return;
              }

              const fitOptions = {
                ...getFitBoundsOptions(dataAnalysis),
                animate: true,
                duration: 1.0,
                easeLinearity: 0.1,
              };

              leafletMapRef.current.fitBounds(bounds, fitOptions);
            }
          }}>
          <RotateCcw className='h-4 w-4' />
          Reset Zoom
        </Button>
      </div>
    </div>
  );
}

export default FleetMap;
